import { useEffect, useState } from "react";
import "@/App.css";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, ExternalLink, Info, ArrowLeft, GraduationCap, Cpu, Landmark, ChevronRight, Smartphone, Globe, Gamepad2, TrendingUp, Building2, Coins, Plus, Trash2, Users, Shield } from "lucide-react";
import { BrowserRouter, Routes, Route, useNavigate, useParams, Link } from "react-router-dom";

// Logo component
const Logo = ({ className = "", size = "large" }) => {
  const sizes = {
    large: "text-5xl md:text-6xl lg:text-7xl",
    medium: "text-3xl md:text-4xl",
    small: "text-xl md:text-2xl"
  };
  return (
    <div className={`flex flex-col leading-[0.85] tracking-tight font-bold text-white select-none text-left ${className}`}>
      <span className={sizes[size]}>Groupe</span>
      <span className={sizes[size]}>Archeon</span>
    </div>
  );
};

const BranchLogo = ({ name, size = "large" }) => {
  const sizes = { large: "text-4xl md:text-5xl lg:text-6xl", medium: "text-3xl md:text-4xl" };
  return (
    <div className="flex flex-col leading-[0.85] tracking-tight font-bold text-white select-none">
      <span className={sizes[size]}>Archeon</span>
      <span className={sizes[size]}>{name}</span>
    </div>
  );
};

const GrainOverlay = () => <div className="grain-overlay" aria-hidden="true" />;
const Vignette = () => <div className="vignette" aria-hidden="true" />;

// Auth hook
const useAuth = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (!storedUser) { navigate('/connexion'); return; }
    setUser(JSON.parse(storedUser));
    setLoading(false);
  }, [navigate]);

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };
  return { user, loading, logout };
};

// Header
const AuthHeader = ({ transparent = false }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  if (!user) return null;
  return (
    <header className={`relative z-10 px-8 py-6 flex justify-between items-center ${transparent ? '' : 'border-b border-zinc-900'}`}>
      <button onClick={() => navigate('/dashboard')} className="hover:opacity-70 transition-opacity duration-300">
        <Logo size="small" />
      </button>
      <div className="flex items-center gap-6">
        <span className="text-zinc-600 text-sm hidden md:block">{user.email}</span>
        <button onClick={logout} className="text-zinc-500 text-sm hover:text-white transition-colors duration-300" data-testid="logout-btn">Déconnexion</button>
      </div>
    </header>
  );
};

// Hero Section
const HeroSection = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  useEffect(() => { const t = setTimeout(() => setIsLoaded(true), 100); return () => clearTimeout(t); }, []);
  return (
    <section data-testid="hero-section" className="relative h-screen w-full flex flex-col items-center justify-center bg-black overflow-hidden">
      <div className="hero-glow" aria-hidden="true" />
      <AnimatePresence>
        {isLoaded && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1], delay: 0.3 }} className="logo-container relative z-10">
            <Logo size="large" data-testid="main-logo" />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

// Footer Section
const FooterSection = () => (
  <footer data-testid="footer-section" className="relative bg-[#020202] border-t border-zinc-900">
    <div className="max-w-7xl mx-auto px-8 py-20 md:py-24">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }} viewport={{ once: true }} className="space-y-6">
          <Logo size="small" />
          <p className="text-zinc-600 text-sm leading-relaxed max-w-xs mt-4">Forging the future in silence.</p>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }} viewport={{ once: true }} className="space-y-6">
          <h3 className="text-zinc-500 text-xs font-medium tracking-[0.2em] uppercase">Légal</h3>
          <nav className="flex flex-col gap-4">
            <a href="/mentions-legales" className="footer-link">Mentions légales</a>
            <a href="/cgu" className="footer-link">Conditions générales</a>
            <a href="/confidentialite" className="footer-link">Politique de confidentialité</a>
          </nav>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.3 }} viewport={{ once: true }} className="space-y-6">
          <h3 className="text-zinc-500 text-xs font-medium tracking-[0.2em] uppercase">Navigation</h3>
          <nav className="flex flex-col gap-4">
            <a href="/a-propos" className="inline-flex items-center gap-2 footer-link"><Info className="w-3.5 h-3.5" />À propos</a>
            <a href="/contact" className="inline-flex items-center gap-2 footer-link"><Mail className="w-3.5 h-3.5" />Contact</a>
            <a href="/presse" className="inline-flex items-center gap-2 footer-link"><ExternalLink className="w-3.5 h-3.5" />Presse</a>
          </nav>
        </motion.div>
      </div>
      <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.5 }} viewport={{ once: true }} className="mt-16 pt-8 border-t border-zinc-900 flex flex-row justify-between items-center">
        <p className="text-zinc-700 text-xs">© {new Date().getFullYear()} Groupe Archeon. Tous droits réservés.</p>
        <a href="/connexion" className="text-zinc-500 text-xs hover:text-white transition-colors duration-300">Connexion</a>
      </motion.div>
    </div>
  </footer>
);

const HomePage = () => (<><GrainOverlay /><Vignette /><main><HeroSection /><FooterSection /></main></>);

// Login Page
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LoginPage = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    try {
      const response = await fetch(`${API}/auth/login`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ username, password }) });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || "Identifiants invalides");
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      navigate('/dashboard');
    } catch (err) { setError(err.message || "Identifiants invalides"); } 
    finally { setIsLoading(false); }
  };

  return (
    <div className="min-h-screen bg-black flex flex-col" data-testid="login-page">
      <GrainOverlay /><Vignette />
      <header className="relative z-10 p-8">
        <button onClick={() => navigate("/")} className="inline-flex items-center gap-2 text-zinc-500 text-sm hover:text-white transition-colors duration-300"><ArrowLeft className="w-4 h-4" />Retour</button>
      </header>
      <main className="flex-1 flex items-center justify-center px-8 pb-24">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="w-full max-w-sm">
          <div className="mb-12"><Logo size="medium" /></div>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label className="block text-zinc-500 text-xs tracking-[0.1em] uppercase">Identifiant</label>
              <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} required className="w-full bg-transparent border border-zinc-800 text-white px-4 py-3 text-sm focus:outline-none focus:border-zinc-600 transition-colors duration-300 placeholder:text-zinc-700" placeholder="Votre identifiant" data-testid="login-username" />
            </div>
            <div className="space-y-2">
              <label className="block text-zinc-500 text-xs tracking-[0.1em] uppercase">Mot de passe</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="w-full bg-transparent border border-zinc-800 text-white px-4 py-3 text-sm focus:outline-none focus:border-zinc-600 transition-colors duration-300 placeholder:text-zinc-700" placeholder="••••••••" data-testid="login-password" />
            </div>
            {error && <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-red-500 text-sm">{error}</motion.p>}
            <button type="submit" disabled={isLoading} className="w-full btn-connexion disabled:opacity-50" data-testid="login-submit">{isLoading ? "Connexion..." : "Se connecter"}</button>
          </form>
        </motion.div>
      </main>
    </div>
  );
};

// Dashboard
const DashboardPage = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  if (loading || !user) return null;

  const branches = [
    { path: '/education', name: 'Education', icon: GraduationCap, description: 'Formation & accompagnement', accent: 'group-hover:text-blue-400' },
    { path: '/technologies', name: 'Technologies', icon: Cpu, description: 'Innovation & développement', accent: 'group-hover:text-emerald-400' },
    { path: '/capital', name: 'Capital', icon: Landmark, description: 'Investissement & patrimoine', accent: 'group-hover:text-amber-400' },
  ];

  return (
    <div className="min-h-screen bg-black flex flex-col" data-testid="dashboard-page">
      <GrainOverlay /><Vignette /><AuthHeader transparent />
      <main className="relative z-10 flex-1 flex flex-col md:flex-row">
        {branches.map((branch, i) => {
          const Icon = branch.icon;
          return (
            <motion.button key={branch.path} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: i * 0.15 }} onClick={() => navigate(branch.path)} className="group flex-1 flex flex-col items-center justify-center p-8 md:p-12 border-b md:border-b-0 md:border-r border-zinc-900 last:border-0 transition-all duration-700 hover:bg-zinc-950" data-testid={`branch-${branch.name.toLowerCase()}`}>
              <Icon className={`w-12 h-12 md:w-16 md:h-16 text-zinc-800 mb-8 transition-colors duration-500 ${branch.accent}`} />
              <p className="text-zinc-600 text-xs tracking-[0.2em] uppercase mb-3">Archeon</p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">{branch.name}</h2>
              <p className="text-zinc-600 text-sm mb-8">{branch.description}</p>
              <div className="flex items-center gap-2 text-zinc-700 text-sm group-hover:text-white transition-all duration-500">
                <span className="tracking-[0.1em] uppercase">Explorer</span>
                <ChevronRight className="w-4 h-4 group-hover:translate-x-2 transition-transform duration-500" />
              </div>
            </motion.button>
          );
        })}
      </main>
    </div>
  );
};

// Education Page
const EducationPage = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  if (loading || !user) return null;

  const verticals = [
    { id: 'sante', name: 'Santé', description: 'Formation aux métiers de la santé', projects: 1, status: 'active' },
    { id: 'droit', name: 'Droit', description: 'Formation juridique', projects: 1, status: 'creation' },
    { id: 'business', name: 'Business', description: 'Comptabilité, gestion et finance', projects: 2, status: 'future' },
  ];

  return (
    <div className="min-h-screen bg-black">
      <GrainOverlay /><Vignette /><AuthHeader />
      <main className="relative z-10 max-w-6xl mx-auto px-8 py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="space-y-16">
          <div className="space-y-6">
            <BranchLogo name="Education" />
            <p className="text-zinc-500 max-w-2xl leading-relaxed text-lg">Programmes de formation et d'accompagnement vers l'excellence.</p>
          </div>
          <div className="space-y-8">
            <h2 className="text-zinc-600 text-xs tracking-[0.2em] uppercase">Domaines</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-800">
              {verticals.map((v) => (
                <button key={v.id} onClick={() => navigate(`/education/${v.id}`)} className="bg-black p-8 md:p-10 group hover:bg-zinc-950 transition-colors duration-500 text-left">
                  <GraduationCap className="w-8 h-8 text-zinc-700 mb-6 group-hover:text-blue-400 transition-colors duration-500" />
                  <h3 className="text-white text-xl font-semibold mb-1">{v.name}</h3>
                  <p className="text-zinc-600 text-sm mb-4">{v.description}</p>
                  <div className="flex items-center justify-between">
                    <span className={`text-xs tracking-[0.15em] uppercase ${v.status === 'active' ? 'text-emerald-500/70' : v.status === 'creation' ? 'text-amber-500/70' : 'text-zinc-600'}`}>
                      {v.status === 'active' ? 'Actif' : v.status === 'creation' ? 'En création' : 'Projet futur'}
                    </span>
                    <div className="flex items-center gap-3">
                      <span className="text-zinc-700 text-xs">{v.projects} projet{v.projects > 1 ? 's' : ''}</span>
                      <ChevronRight className="w-4 h-4 text-zinc-700 group-hover:text-white group-hover:translate-x-1 transition-all duration-300" />
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

// Education Vertical Page
const EducationVerticalPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user, loading } = useAuth();
  
  const data = {
    sante: {
      name: 'Santé',
      description: 'Programmes de formation aux métiers de la santé.',
      projects: [
        { id: 'helix', name: 'Helix Education', type: 'Prépa Médecine', status: 'active', statusLabel: 'Actif', description: 'Préparation intensive aux études de santé. PASS et L.AS.' }
      ]
    },
    droit: {
      name: 'Droit',
      description: 'Programmes de formation juridique.',
      projects: [
        { id: 'jurix', name: 'Jurix Education', type: 'Prépa Droit', status: 'creation', statusLabel: 'En création', description: 'Formation aux fondamentaux juridiques et préparation aux concours.' }
      ]
    },
    business: {
      name: 'Business',
      description: 'Programmes de formation en comptabilité, gestion et finance.',
      projects: [
        { id: 'compta', name: 'Prépa Comptabilité', type: 'Formation comptable', status: 'future', statusLabel: 'Projet futur', description: 'Préparation aux diplômes comptables. DCG, DSCG.' },
        { id: 'finance', name: 'Prépa Finance', type: 'Certification AMF', status: 'future', statusLabel: 'Projet futur', description: 'Préparation à la certification AMF et aux métiers de la finance.' }
      ]
    }
  };

  const vertical = data[id];
  if (loading || !user) return null;
  if (!vertical) { navigate('/education'); return null; }

  return (
    <div className="min-h-screen bg-black">
      <GrainOverlay /><Vignette /><AuthHeader />
      <main className="relative z-10 max-w-6xl mx-auto px-8 py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="space-y-12">
          <button onClick={() => navigate('/education')} className="inline-flex items-center gap-2 text-zinc-500 text-sm hover:text-white transition-colors duration-300"><ArrowLeft className="w-4 h-4" />Archeon Education</button>
          
          <div className="space-y-4">
            <div className="flex items-center gap-4"><GraduationCap className="w-10 h-10 text-blue-400" /></div>
            <h1 className="text-4xl md:text-5xl font-bold text-white">{vertical.name}</h1>
            <p className="text-zinc-500 text-lg">{vertical.description}</p>
          </div>

          <div className="space-y-6">
            <h2 className="text-zinc-600 text-xs tracking-[0.2em] uppercase">Projets ({vertical.projects.length})</h2>
            <div className="grid grid-cols-1 gap-px bg-zinc-800">
              {vertical.projects.map((p) => (
                <button key={p.id} onClick={() => navigate(`/education/${id}/${p.id}`)} className="bg-black p-8 group hover:bg-zinc-950 transition-colors duration-500 text-left">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-white text-xl font-semibold">{p.name}</h3>
                    <span className={`text-xs tracking-[0.15em] uppercase ${p.status === 'active' ? 'text-emerald-500/70' : p.status === 'creation' ? 'text-amber-500/70' : 'text-zinc-600'}`}>{p.statusLabel}</span>
                  </div>
                  <p className="text-zinc-500 text-sm mb-2">{p.type}</p>
                  <p className="text-zinc-600 text-sm mb-4">{p.description}</p>
                  <ChevronRight className="w-4 h-4 text-zinc-700 group-hover:text-white group-hover:translate-x-1 transition-all duration-300" />
                </button>
              ))}
            </div>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

// Education Project Detail Page
const EducationProjectPage = () => {
  const navigate = useNavigate();
  const { id, projectId } = useParams();
  const { user, loading } = useAuth();
  
  const allProjects = {
    helix: { name: 'Helix Education', type: 'Prépa Médecine', vertical: 'sante', verticalName: 'Santé', status: 'Actif', statusColor: 'text-emerald-500/70', description: 'Branche médicale d\'Archeon Education, spécialisée dans la préparation aux études de santé.', details: ['Préparation PASS', 'Préparation L.AS', 'Accompagnement personnalisé', 'Méthodologie et organisation', 'Cours particuliers et collectifs'] },
    jurix: { name: 'Jurix Education', type: 'Prépa Droit', vertical: 'droit', verticalName: 'Droit', status: 'En création', statusColor: 'text-amber-500/70', description: 'Future branche juridique d\'Archeon Education, dédiée à la préparation aux études de droit.', details: ['Préparation aux concours d\'entrée', 'Formation aux fondamentaux juridiques', 'Méthodologie dissertation', 'Commentaire d\'arrêt', 'Cas pratiques'] },
    compta: { name: 'Prépa Comptabilité', type: 'Formation comptable', vertical: 'business', verticalName: 'Business', status: 'Projet futur', statusColor: 'text-zinc-600', description: 'Future branche comptable pour la préparation aux diplômes de comptabilité.', details: ['Préparation DCG', 'Préparation DSCG', 'Accompagnement expertise comptable'] },
    finance: { name: 'Prépa Finance', type: 'Certification AMF', vertical: 'business', verticalName: 'Business', status: 'Projet futur', statusColor: 'text-zinc-600', description: 'Programme de préparation à la certification AMF et aux métiers de la finance de marché.', details: ['Certification AMF', 'Fondamentaux des marchés financiers', 'Réglementation et conformité', 'Instruments financiers'] }
  };

  const project = allProjects[projectId];
  if (loading || !user) return null;
  if (!project) { navigate(`/education/${id}`); return null; }

  return (
    <div className="min-h-screen bg-black">
      <GrainOverlay /><Vignette /><AuthHeader />
      <main className="relative z-10 max-w-4xl mx-auto px-8 py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="space-y-12">
          <button onClick={() => navigate(`/education/${id}`)} className="inline-flex items-center gap-2 text-zinc-500 text-sm hover:text-white transition-colors duration-300"><ArrowLeft className="w-4 h-4" />{project.verticalName}</button>
          <div className="space-y-4">
            <div className="flex items-center gap-4"><GraduationCap className="w-10 h-10 text-blue-400" /><span className={`text-xs tracking-[0.15em] uppercase ${project.statusColor}`}>{project.status}</span></div>
            <h1 className="text-4xl md:text-5xl font-bold text-white">{project.name}</h1>
            <p className="text-zinc-500 text-lg">{project.type}</p>
          </div>
          <p className="text-zinc-400 leading-relaxed text-lg border-l-2 border-zinc-800 pl-6">{project.description}</p>
          <div className="space-y-6">
            <h2 className="text-zinc-600 text-xs tracking-[0.2em] uppercase">Programmes & Services</h2>
            <ul className="space-y-3">{project.details.map((d, i) => <li key={i} className="flex items-center gap-3 text-zinc-400"><span className="w-1.5 h-1.5 bg-zinc-700 rounded-full"></span>{d}</li>)}</ul>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

// Technologies Page
const TechnologiesPage = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  if (loading || !user) return null;

  const services = [
    { path: '/technologies/apps', name: 'Applications', icon: Smartphone, description: 'Développement sur mesure.', projects: 2, status: 'active', statusLabel: 'Actif' },
    { path: '/technologies/web', name: 'Sites Web', icon: Globe, description: 'Création et refonte.', projects: '—', status: 'active', statusLabel: 'Actif' },
    { path: '/technologies/systems', name: 'Systèmes', icon: Cpu, description: 'Architecture et optimisation.', projects: '—', status: 'active', statusLabel: 'Actif' },
    { path: '/technologies/privacy', name: 'Privacy', icon: Shield, description: 'Configuration et sécurisation.', projects: '—', status: 'active', statusLabel: 'Actif' },
    { path: '/technologies/gaming', name: 'Gaming', icon: Gamepad2, description: 'Serveurs de jeux.', projects: 7, status: 'inactive', statusLabel: 'Inactif' },
    { path: '/technologies/community', name: 'Communautés', icon: Users, description: 'Projets Discord.', projects: 3, status: 'inactive', statusLabel: 'Inactif' },
  ];

  return (
    <div className="min-h-screen bg-black" data-testid="technologies-page">
      <GrainOverlay /><Vignette /><AuthHeader />
      <main className="relative z-10 max-w-6xl mx-auto px-8 py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="space-y-16">
          <div className="space-y-6">
            <BranchLogo name="Technologies" />
            <p className="text-zinc-500 max-w-2xl leading-relaxed text-lg">Solutions techniques et numériques.</p>
          </div>
          <div className="space-y-8">
            <h2 className="text-zinc-600 text-xs tracking-[0.2em] uppercase">Services</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-800">
              {services.map((s) => {
                const Icon = s.icon;
                return (
                  <button key={s.path} onClick={() => navigate(s.path)} className="bg-black p-8 md:p-10 group hover:bg-zinc-950 transition-colors duration-500 text-left">
                    <Icon className={`w-8 h-8 text-zinc-700 mb-6 transition-colors duration-500 ${s.status === 'active' ? 'group-hover:text-emerald-400' : 'group-hover:text-zinc-500'}`} />
                    <h3 className="text-white text-xl font-semibold mb-1">{s.name}</h3>
                    <p className="text-zinc-600 text-sm mb-4">{s.description}</p>
                    <div className="flex items-center justify-between">
                      <span className={`text-xs tracking-[0.15em] uppercase ${s.status === 'active' ? 'text-emerald-500/70' : 'text-zinc-700'}`}>{s.statusLabel}</span>
                      <div className="flex items-center gap-3">
                        <span className="text-zinc-700 text-xs">{s.projects} projet{s.projects !== 1 && s.projects !== '—' ? 's' : ''}</span>
                        <ChevronRight className="w-4 h-4 text-zinc-700 group-hover:text-white group-hover:translate-x-1 transition-all duration-300" />
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

// Tech Apps Page
const TechAppsPage = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  if (loading || !user) return null;
  const projects = [
    { id: 'castorama', name: 'Annuaire Castorama', description: 'Application annuaire interne pour magasin', year: '2024' },
    { id: 'bodycount', name: 'Bodycount', description: 'Journal de suivi personnel et statistiques', year: '2024' }
  ];

  return (
    <div className="min-h-screen bg-black">
      <GrainOverlay /><Vignette /><AuthHeader />
      <main className="relative z-10 max-w-6xl mx-auto px-8 py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="space-y-12">
          <button onClick={() => navigate('/technologies')} className="inline-flex items-center gap-2 text-zinc-500 text-sm hover:text-white transition-colors duration-300"><ArrowLeft className="w-4 h-4" />Archeon Technologies</button>
          <div className="space-y-4">
            <div className="flex items-center gap-4"><Smartphone className="w-10 h-10 text-emerald-400" /><span className="text-emerald-500/70 text-xs tracking-[0.15em] uppercase">Actif</span></div>
            <h1 className="text-4xl md:text-5xl font-bold text-white">Applications</h1>
            <p className="text-zinc-500 text-lg">Applications mobiles et web sur mesure.</p>
          </div>
          <div className="space-y-6">
            <h2 className="text-zinc-600 text-xs tracking-[0.2em] uppercase">Projets ({projects.length})</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-800">
              {projects.map((p) => (
                <button key={p.id} onClick={() => navigate(`/technologies/apps/${p.id}`)} className="bg-black p-8 group hover:bg-zinc-950 transition-colors duration-500 text-left">
                  <div className="flex items-center justify-between mb-4"><span className="text-zinc-700 text-xs">{p.year}</span><span className="text-emerald-500/70 text-xs tracking-[0.1em] uppercase">Actif</span></div>
                  <h3 className="text-white text-xl font-semibold mb-2">{p.name}</h3>
                  <p className="text-zinc-600 text-sm mb-4">{p.description}</p>
                  <ChevronRight className="w-4 h-4 text-zinc-700 group-hover:text-white group-hover:translate-x-1 transition-all duration-300" />
                </button>
              ))}
            </div>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

// Tech App Detail
const TechAppDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user, loading } = useAuth();
  const data = {
    castorama: { name: 'Annuaire Castorama', description: 'Application annuaire interne pour un magasin Castorama.', features: ['Recherche rapide par nom ou service', 'Fiches contacts détaillées', 'Organisation par rayons', 'Interface intuitive'], tech: ['React', 'Node.js', 'MongoDB'] },
    bodycount: { name: 'Bodycount', description: 'Application de journal personnel permettant le suivi et statistiques. Interface discrète et sécurisée.', features: ['Journal de suivi personnel', 'Statistiques et analytics', 'Interface discrète', 'Données chiffrées'], tech: ['React Native', 'FastAPI', 'PostgreSQL'] }
  };
  const item = data[id];
  if (loading || !user) return null;
  if (!item) { navigate('/technologies/apps'); return null; }

  return (
    <div className="min-h-screen bg-black">
      <GrainOverlay /><Vignette /><AuthHeader />
      <main className="relative z-10 max-w-4xl mx-auto px-8 py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="space-y-12">
          <button onClick={() => navigate('/technologies/apps')} className="inline-flex items-center gap-2 text-zinc-500 text-sm hover:text-white transition-colors duration-300"><ArrowLeft className="w-4 h-4" />Applications</button>
          <div className="space-y-4">
            <div className="flex items-center gap-4"><Smartphone className="w-10 h-10 text-emerald-400" /><span className="text-emerald-500/70 text-xs tracking-[0.15em] uppercase">Actif</span></div>
            <h1 className="text-4xl md:text-5xl font-bold text-white">{item.name}</h1>
          </div>
          <p className="text-zinc-400 leading-relaxed text-lg border-l-2 border-zinc-800 pl-6">{item.description}</p>
          <div className="space-y-6">
            <h2 className="text-zinc-600 text-xs tracking-[0.2em] uppercase">Fonctionnalités</h2>
            <ul className="space-y-3">{item.features.map((f, i) => <li key={i} className="flex items-center gap-3 text-zinc-400"><span className="w-1.5 h-1.5 bg-emerald-500/50 rounded-full"></span>{f}</li>)}</ul>
          </div>
          <div className="space-y-6">
            <h2 className="text-zinc-600 text-xs tracking-[0.2em] uppercase">Technologies</h2>
            <div className="flex flex-wrap gap-2">{item.tech.map((t, i) => <span key={i} className="px-3 py-1.5 bg-zinc-900 text-zinc-400 text-sm">{t}</span>)}</div>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

// Tech Web Page
const TechWebPage = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  if (loading || !user) return null;

  return (
    <div className="min-h-screen bg-black">
      <GrainOverlay /><Vignette /><AuthHeader />
      <main className="relative z-10 max-w-6xl mx-auto px-8 py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="space-y-12">
          <button onClick={() => navigate('/technologies')} className="inline-flex items-center gap-2 text-zinc-500 text-sm hover:text-white transition-colors duration-300"><ArrowLeft className="w-4 h-4" />Archeon Technologies</button>
          <div className="space-y-4">
            <div className="flex items-center gap-4"><Globe className="w-10 h-10 text-emerald-400" /><span className="text-emerald-500/70 text-xs tracking-[0.15em] uppercase">Actif</span></div>
            <h1 className="text-4xl md:text-5xl font-bold text-white">Sites Web</h1>
            <p className="text-zinc-500 text-lg">Création et refonte de sites web.</p>
          </div>
          <div className="space-y-6">
            <h2 className="text-zinc-600 text-xs tracking-[0.2em] uppercase">Services</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-800">
              {[{ name: 'Sites Vitrines', desc: 'Présentation et visibilité' }, { name: 'E-commerce', desc: 'Vente en ligne' }, { name: 'Applications Web', desc: 'Solutions sur mesure' }, { name: 'Refonte & Maintenance', desc: 'Évolution et support' }].map((s, i) => (
                <div key={i} className="bg-black p-8 hover:bg-zinc-950 transition-colors duration-500">
                  <h3 className="text-white text-lg font-semibold mb-2">{s.name}</h3>
                  <p className="text-zinc-600 text-sm">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

// Tech Systems Page
const TechSystemsPage = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  if (loading || !user) return null;

  return (
    <div className="min-h-screen bg-black">
      <GrainOverlay /><Vignette /><AuthHeader />
      <main className="relative z-10 max-w-6xl mx-auto px-8 py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="space-y-12">
          <button onClick={() => navigate('/technologies')} className="inline-flex items-center gap-2 text-zinc-500 text-sm hover:text-white transition-colors duration-300"><ArrowLeft className="w-4 h-4" />Archeon Technologies</button>
          <div className="space-y-4">
            <div className="flex items-center gap-4"><Cpu className="w-10 h-10 text-emerald-400" /><span className="text-emerald-500/70 text-xs tracking-[0.15em] uppercase">Actif</span></div>
            <h1 className="text-4xl md:text-5xl font-bold text-white">Systèmes</h1>
            <p className="text-zinc-500 text-lg">Architecture, conception et optimisation de systèmes.</p>
          </div>
          <div className="space-y-6">
            <h2 className="text-zinc-600 text-xs tracking-[0.2em] uppercase">Services</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-800">
              <div className="bg-black p-8 hover:bg-zinc-950 transition-colors duration-500">
                <h3 className="text-white text-lg font-semibold mb-2">Architecture réseau</h3>
                <p className="text-zinc-600 text-sm">Conception et déploiement d'infrastructures réseau.</p>
              </div>
              <div className="bg-black p-8 hover:bg-zinc-950 transition-colors duration-500">
                <h3 className="text-white text-lg font-semibold mb-2">Architecture système</h3>
                <p className="text-zinc-600 text-sm">Conception de systèmes d'information.</p>
              </div>
              <div className="bg-black p-8 hover:bg-zinc-950 transition-colors duration-500">
                <h3 className="text-white text-lg font-semibold mb-2">Optimisation de process</h3>
                <p className="text-zinc-600 text-sm">Restructuration et amélioration des processus internes.</p>
              </div>
              <div className="bg-black p-8 hover:bg-zinc-950 transition-colors duration-500">
                <h3 className="text-white text-lg font-semibold mb-2">Outils sur mesure</h3>
                <p className="text-zinc-600 text-sm">Applications internes, Excel avancés, automatisations.</p>
              </div>
            </div>
          </div>
          <div className="space-y-6">
            <h2 className="text-zinc-600 text-xs tracking-[0.2em] uppercase">Approche</h2>
            <p className="text-zinc-400 leading-relaxed border-l-2 border-zinc-800 pl-6">
              Analyse des besoins, conception d'architecture, implémentation et documentation. Solutions pragmatiques adaptées au contexte.
            </p>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

// Tech Privacy Page
const TechPrivacyPage = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  if (loading || !user) return null;

  return (
    <div className="min-h-screen bg-black">
      <GrainOverlay /><Vignette /><AuthHeader />
      <main className="relative z-10 max-w-6xl mx-auto px-8 py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="space-y-12">
          <button onClick={() => navigate('/technologies')} className="inline-flex items-center gap-2 text-zinc-500 text-sm hover:text-white transition-colors duration-300"><ArrowLeft className="w-4 h-4" />Archeon Technologies</button>
          <div className="space-y-4">
            <div className="flex items-center gap-4"><Shield className="w-10 h-10 text-emerald-400" /><span className="text-emerald-500/70 text-xs tracking-[0.15em] uppercase">Actif</span></div>
            <h1 className="text-4xl md:text-5xl font-bold text-white">Privacy</h1>
            <p className="text-zinc-500 text-lg">Configuration et sécurisation de matériel et logiciels.</p>
          </div>
          <div className="space-y-6">
            <h2 className="text-zinc-600 text-xs tracking-[0.2em] uppercase">Services</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-800">
              <div className="bg-black p-8 hover:bg-zinc-950 transition-colors duration-500">
                <h3 className="text-white text-lg font-semibold mb-2">Hardware</h3>
                <p className="text-zinc-600 text-sm mb-4">Configuration de machines sécurisées.</p>
                <ul className="space-y-2 text-zinc-500 text-sm">
                  <li>• ThinkPad T440p — Coreboot</li>
                  <li>• Pixel — GrapheneOS</li>
                </ul>
              </div>
              <div className="bg-black p-8 hover:bg-zinc-950 transition-colors duration-500">
                <h3 className="text-white text-lg font-semibold mb-2">Software</h3>
                <p className="text-zinc-600 text-sm mb-4">Installation et configuration d'outils.</p>
                <ul className="space-y-2 text-zinc-500 text-sm">
                  <li>• Navigation TOR</li>
                  <li>• XMR Cold Wallet</li>
                  <li>• Clés PGP</li>
                </ul>
              </div>
            </div>
          </div>
          <div className="space-y-6">
            <h2 className="text-zinc-600 text-xs tracking-[0.2em] uppercase">Approche</h2>
            <p className="text-zinc-400 leading-relaxed border-l-2 border-zinc-800 pl-6">
              Configuration complète pour une utilisation quotidienne en toute discrétion. Matériel et logiciels optimisés pour la confidentialité.
            </p>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

// Tech Gaming Page
const TechGamingPage = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  if (loading || !user) return null;

  const games = [
    { 
      id: 'gmod', 
      name: 'Garry\'s Mod', 
      project: 'NoctuaRP',
      servers: 4,
      status: 'inactive',
      period: '2020-2022'
    },
    { 
      id: 'minecraft', 
      name: 'Minecraft', 
      project: 'Serveurs MC',
      servers: 2,
      status: 'inactive',
      period: '—'
    },
    { 
      id: 'rust', 
      name: 'Rust', 
      project: 'Serveur Rust',
      servers: 1,
      status: 'inactive',
      period: '—'
    },
  ];

  return (
    <div className="min-h-screen bg-black">
      <GrainOverlay /><Vignette /><AuthHeader />
      <main className="relative z-10 max-w-6xl mx-auto px-8 py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="space-y-12">
          <button onClick={() => navigate('/technologies')} className="inline-flex items-center gap-2 text-zinc-500 text-sm hover:text-white transition-colors duration-300"><ArrowLeft className="w-4 h-4" />Archeon Technologies</button>
          <div className="space-y-4">
            <div className="flex items-center gap-4"><Gamepad2 className="w-10 h-10 text-zinc-600" /><span className="text-zinc-700 text-xs tracking-[0.15em] uppercase">Inactif</span></div>
            <h1 className="text-4xl md:text-5xl font-bold text-white">Gaming</h1>
            <p className="text-zinc-500 text-lg">Création et gestion de serveurs de jeux.</p>
          </div>
          <div className="space-y-6">
            <h2 className="text-zinc-600 text-xs tracking-[0.2em] uppercase">Projets</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-800">
              {games.map((g) => (
                <button key={g.id} onClick={() => navigate(`/technologies/gaming/${g.id}`)} className="bg-black p-8 group hover:bg-zinc-950 transition-colors duration-500 text-left">
                  <Gamepad2 className="w-6 h-6 text-zinc-700 mb-4" />
                  <h3 className="text-white text-xl font-semibold mb-1">{g.name}</h3>
                  <p className="text-zinc-600 text-sm mb-4">{g.project}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-700 text-xs">{g.servers} serveur{g.servers > 1 ? 's' : ''}</span>
                    <ChevronRight className="w-4 h-4 text-zinc-700 group-hover:text-white group-hover:translate-x-1 transition-all duration-300" />
                  </div>
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 pt-8 border-t border-zinc-900">
            {[{ label: 'Jeux', value: '3' }, { label: 'Serveurs', value: '7' }, { label: 'Statut', value: 'Fermé' }, { label: 'Période', value: '2018-2022' }].map((s, i) => <div key={i}><p className="text-white text-3xl font-bold mb-2">{s.value}</p><p className="text-zinc-700 text-xs tracking-[0.1em] uppercase">{s.label}</p></div>)}
          </div>
        </motion.div>
      </main>
    </div>
  );
};

// Tech Gaming Detail
const TechGamingDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user, loading } = useAuth();
  
  const data = {
    gmod: {
      name: 'Garry\'s Mod',
      project: 'NoctuaRP',
      description: 'Projet de serveurs Garry\'s Mod sous la marque NoctuaRP. Quatre serveurs roleplay et détente.',
      servers: [
        { id: 'darkrp', name: 'DarkRP', type: 'Roleplay Urbain', slots: 64 },
        { id: 'militaryrp', name: 'MilitaryRP', type: 'Roleplay Militaire', slots: 64 },
        { id: 'murder', name: 'Murder', type: 'Murder Mystery', slots: 24 },
        { id: 'sandbox', name: 'Sandbox', type: 'Construction', slots: 32 },
      ],
      period: '2020-2022'
    },
    minecraft: {
      name: 'Minecraft',
      project: 'Serveurs Minecraft',
      description: 'Deux serveurs Minecraft communautaires.',
      servers: [
        { id: 'survie', name: 'Serveur Survie', type: 'Survival', slots: null },
        { id: 'creatif', name: 'Serveur Créatif', type: 'Creative', slots: null },
      ],
      period: '—'
    },
    rust: {
      name: 'Rust',
      project: 'Serveur Rust',
      description: 'Serveur Rust communautaire.',
      servers: [
        { id: 'main', name: 'Serveur principal', type: 'Vanilla modifié', slots: null },
      ],
      period: '—'
    }
  };

  const item = data[id];
  if (loading || !user) return null;
  if (!item) { navigate('/technologies/gaming'); return null; }

  return (
    <div className="min-h-screen bg-black">
      <GrainOverlay /><Vignette /><AuthHeader />
      <main className="relative z-10 max-w-4xl mx-auto px-8 py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="space-y-12">
          <button onClick={() => navigate('/technologies/gaming')} className="inline-flex items-center gap-2 text-zinc-500 text-sm hover:text-white transition-colors duration-300"><ArrowLeft className="w-4 h-4" />Gaming</button>
          <div className="space-y-4">
            <div className="flex items-center gap-4"><Gamepad2 className="w-10 h-10 text-zinc-600" /><span className="text-zinc-700 text-xs tracking-[0.15em] uppercase">Fermé</span></div>
            <h1 className="text-4xl md:text-5xl font-bold text-white">{item.name}</h1>
            <p className="text-zinc-500 text-lg">{item.project}</p>
          </div>
          <p className="text-zinc-400 leading-relaxed text-lg border-l-2 border-zinc-800 pl-6">{item.description}</p>
          <div className="space-y-6">
            <h2 className="text-zinc-600 text-xs tracking-[0.2em] uppercase">Serveurs ({item.servers.length})</h2>
            <div className={`grid gap-px bg-zinc-800 ${item.servers.length === 1 ? 'grid-cols-1 max-w-md' : 'grid-cols-1 md:grid-cols-2'}`}>
              {item.servers.map((s) => (
                <button key={s.id} onClick={() => navigate(`/technologies/gaming/${id}/${s.id}`)} className="bg-black p-6 text-left group hover:bg-zinc-950 transition-colors duration-300">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-white font-semibold mb-1">{s.name}</h3>
                      <p className="text-zinc-600 text-sm">{s.type}</p>
                      {s.slots && <p className="text-zinc-700 text-xs mt-2">{s.slots} slots</p>}
                    </div>
                    <ChevronRight className="w-4 h-4 text-zinc-700 group-hover:text-white group-hover:translate-x-1 transition-all duration-300" />
                  </div>
                </button>
              ))}
            </div>
          </div>
          {item.period !== '—' && (
            <div className="pt-8 border-t border-zinc-900">
              <p className="text-zinc-700 text-sm">Période d'activité : {item.period}</p>
            </div>
          )}
        </motion.div>
      </main>
    </div>
  );
};

// Tech Gaming Server Detail Page
const TechGamingServerPage = () => {
  const navigate = useNavigate();
  const { id, serverId } = useParams();
  const { user, loading } = useAuth();
  
  const allServers = {
    gmod: {
      darkrp: { name: 'DarkRP', type: 'Roleplay Urbain', slots: 64, game: 'Garry\'s Mod', description: 'Serveur DarkRP classique avec économie, jobs, et système de gangs.', features: ['Système économique complet', 'Jobs variés (policier, gangster, commerçant...)', 'Système de propriétés', 'Organisations et gangs', 'Events réguliers'] },
      militaryrp: { name: 'MilitaryRP', type: 'Roleplay Militaire', slots: 64, game: 'Garry\'s Mod', description: 'Serveur roleplay militaire avec hiérarchie, formations et missions.', features: ['Hiérarchie militaire réaliste', 'Système de formations', 'Missions et opérations', 'Véhicules militaires', 'Spécialisations (infanterie, blindés, aviation...)'] },
      murder: { name: 'Murder', type: 'Murder Mystery', slots: 24, game: 'Garry\'s Mod', description: 'Mode de jeu Murder Mystery où un assassin doit éliminer les innocents sans se faire repérer.', features: ['Mode classique Murder', 'Maps variées', 'Système de points', 'Cosmétiques déblocables'] },
      sandbox: { name: 'Sandbox', type: 'Construction', slots: 32, game: 'Garry\'s Mod', description: 'Serveur Sandbox pour la construction libre et l\'expérimentation avec les outils Garry\'s Mod.', features: ['Construction libre', 'Addons populaires installés', 'Wiremod et outils avancés', 'Duplication et sauvegarde'] }
    },
    minecraft: {
      survie: { name: 'Serveur Survie', type: 'Survival', slots: null, game: 'Minecraft', description: 'Serveur survie vanilla avec quelques plugins qualité de vie.', features: ['Mode survie classique', 'Économie de base', 'Protection des terrains', 'Communauté active'] },
      creatif: { name: 'Serveur Créatif', type: 'Creative', slots: null, game: 'Minecraft', description: 'Serveur créatif pour les constructions libres.', features: ['Mode créatif', 'WorldEdit disponible', 'Plots personnels', 'Showcase des builds'] }
    },
    rust: {
      main: { name: 'Serveur principal', type: 'Vanilla modifié', slots: null, game: 'Rust', description: 'Serveur Rust vanilla avec quelques modifications pour améliorer l\'expérience.', features: ['Taux de gather modifié', 'Kits de base', 'Wipes réguliers', 'Anti-cheat actif'] }
    }
  };

  const server = allServers[id]?.[serverId];
  if (loading || !user) return null;
  if (!server) { navigate(`/technologies/gaming/${id}`); return null; }

  return (
    <div className="min-h-screen bg-black">
      <GrainOverlay /><Vignette /><AuthHeader />
      <main className="relative z-10 max-w-4xl mx-auto px-8 py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="space-y-12">
          <button onClick={() => navigate(`/technologies/gaming/${id}`)} className="inline-flex items-center gap-2 text-zinc-500 text-sm hover:text-white transition-colors duration-300"><ArrowLeft className="w-4 h-4" />{server.game}</button>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <Gamepad2 className="w-10 h-10 text-zinc-600" />
              <span className="text-zinc-700 text-xs tracking-[0.15em] uppercase">Fermé</span>
              {server.slots && <span className="text-zinc-700 text-xs">{server.slots} slots</span>}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white">{server.name}</h1>
            <p className="text-zinc-500 text-lg">{server.type}</p>
          </div>
          <p className="text-zinc-400 leading-relaxed text-lg border-l-2 border-zinc-800 pl-6">{server.description}</p>
          <div className="space-y-6">
            <h2 className="text-zinc-600 text-xs tracking-[0.2em] uppercase">Caractéristiques</h2>
            <ul className="space-y-3">{server.features.map((f, i) => <li key={i} className="flex items-center gap-3 text-zinc-400"><span className="w-1.5 h-1.5 bg-zinc-700 rounded-full"></span>{f}</li>)}</ul>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

// Tech Community Page
const TechCommunityPage = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  if (loading || !user) return null;

  const communities = [
    { id: 'community1', name: 'Communauté 1', members: '—', status: 'inactive' },
    { id: 'community2', name: 'Communauté 2', members: '—', status: 'inactive' },
    { id: 'community3', name: 'Communauté 3', members: '—', status: 'inactive' },
  ];

  return (
    <div className="min-h-screen bg-black">
      <GrainOverlay /><Vignette /><AuthHeader />
      <main className="relative z-10 max-w-6xl mx-auto px-8 py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="space-y-12">
          <button onClick={() => navigate('/technologies')} className="inline-flex items-center gap-2 text-zinc-500 text-sm hover:text-white transition-colors duration-300"><ArrowLeft className="w-4 h-4" />Archeon Technologies</button>
          <div className="space-y-4">
            <div className="flex items-center gap-4"><Users className="w-10 h-10 text-zinc-600" /><span className="text-zinc-700 text-xs tracking-[0.15em] uppercase">Inactif</span></div>
            <h1 className="text-4xl md:text-5xl font-bold text-white">Communautés</h1>
            <p className="text-zinc-500 text-lg">Projets communautaires Discord.</p>
          </div>
          <div className="space-y-6">
            <h2 className="text-zinc-600 text-xs tracking-[0.2em] uppercase">Projets ({communities.length})</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-800">
              {communities.map((c) => (
                <div key={c.id} className="bg-black p-8 hover:bg-zinc-950 transition-colors duration-500">
                  <Users className="w-6 h-6 text-zinc-700 mb-4" />
                  <h3 className="text-white text-xl font-semibold mb-2">{c.name}</h3>
                  <span className="text-zinc-700 text-xs tracking-[0.15em] uppercase">Fermé</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

// Capital Page
const CapitalPage = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  if (loading || !user) return null;

  const scis = [
    { id: 'laline', name: 'Laline Investissement', type: 'SCI' },
    { id: 'rotanline', name: 'SCI Rotanline', type: 'SCI' }
  ];
  const investments = [
    { id: 'metaux', name: 'Métaux précieux', desc: 'Or, Argent' },
    { id: 'actions', name: 'Actions', desc: 'Marchés financiers' },
    { id: 'crypto', name: 'Crypto', desc: 'Actifs numériques' },
    { id: 'venture', name: 'Venture', desc: 'Capital risque' },
    { id: 'immobilier', name: 'Immobilier', desc: 'Pierre physique' },
  ];

  return (
    <div className="min-h-screen bg-black" data-testid="capital-page">
      <GrainOverlay /><Vignette /><AuthHeader />
      <main className="relative z-10 max-w-6xl mx-auto px-8 py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="space-y-16">
          <div className="space-y-6">
            <BranchLogo name="Capital" />
            <p className="text-zinc-500 max-w-2xl leading-relaxed text-lg">Gestion et diversification patrimoniale.</p>
          </div>
          <div className="space-y-8">
            <h2 className="text-zinc-600 text-xs tracking-[0.2em] uppercase">Sociétés Civiles Immobilières</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-800">
              {scis.map((sci) => (
                <button key={sci.id} onClick={() => navigate(`/capital/${sci.id}`)} className="bg-black p-8 md:p-10 group hover:bg-zinc-950 transition-colors duration-500 text-left">
                  <Landmark className="w-8 h-8 text-zinc-700 mb-6 group-hover:text-amber-400 transition-colors duration-500" />
                  <h3 className="text-white text-xl font-semibold mb-3">{sci.name}</h3>
                  <div className="flex items-center justify-between">
                    <span className="text-emerald-500/70 text-xs tracking-[0.15em] uppercase">Active</span>
                    <ChevronRight className="w-4 h-4 text-zinc-700 group-hover:text-white group-hover:translate-x-1 transition-all duration-300" />
                  </div>
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-8">
            <h2 className="text-zinc-600 text-xs tracking-[0.2em] uppercase">Investissements en nom propre</h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-zinc-800">
              {investments.map((inv) => (
                <button key={inv.id} onClick={() => navigate(`/capital/${inv.id}`)} className="bg-black p-6 group hover:bg-zinc-950 transition-colors duration-500 text-center">
                  <h3 className="text-white text-sm font-semibold mb-1">{inv.name}</h3>
                  <p className="text-zinc-700 text-xs mb-2">{inv.desc}</p>
                  <ChevronRight className="w-3 h-3 text-zinc-800 mx-auto group-hover:text-white transition-colors duration-300" />
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-8 pt-8 border-t border-zinc-900">
            {[{ label: 'SCI', value: '2' }, { label: 'Classes d\'actifs', value: '5' }, { label: 'Stratégie', value: 'Long terme' }].map((s, i) => <div key={i}><p className="text-white text-4xl font-bold mb-2">{s.value}</p><p className="text-zinc-700 text-xs tracking-[0.1em] uppercase">{s.label}</p></div>)}
          </div>
        </motion.div>
      </main>
    </div>
  );
};

// Capital Detail Page
const CapitalDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user, loading } = useAuth();
  
  const data = {
    laline: { name: 'Laline Investissement', type: 'SCI', icon: Building2, status: 'Active', statusColor: 'text-emerald-500/70', description: 'Société civile immobilière dédiée à l\'acquisition et la gestion de biens immobiliers.', details: ['Acquisition de biens', 'Gestion locative', 'Optimisation patrimoniale'] },
    rotanline: { name: 'SCI Rotanline', type: 'SCI', icon: Building2, status: 'Active', statusColor: 'text-emerald-500/70', description: 'Société civile immobilière pour l\'investissement immobilier.', details: ['Acquisition de biens', 'Gestion locative', 'Stratégie long terme'] },
    metaux: { name: 'Métaux précieux', type: 'Investissement', icon: Coins, status: 'Actif', statusColor: 'text-emerald-500/70', description: 'Investissement en métaux précieux pour la diversification et la protection du patrimoine.', details: ['Or physique', 'Argent physique', 'Stockage sécurisé', 'Stratégie de conservation'] },
    actions: { name: 'Actions', type: 'Investissement', icon: TrendingUp, status: 'Actif', statusColor: 'text-emerald-500/70', description: 'Investissement sur les marchés financiers via des actions.', details: ['Actions individuelles', 'ETF', 'Dividendes', 'Croissance long terme'] },
    crypto: { name: 'Crypto-actifs', type: 'Investissement', icon: Coins, status: 'Actif', statusColor: 'text-emerald-500/70', description: 'Exposition aux crypto-monnaies et actifs numériques.', details: ['Bitcoin', 'Ethereum', 'Altcoins sélectionnés', 'DeFi'] },
    venture: { name: 'Venture Capital', type: 'Investissement', icon: TrendingUp, status: 'Actif', statusColor: 'text-emerald-500/70', description: 'Investissement en capital risque dans des startups et projets innovants.', details: ['Seed stage', 'Early stage', 'Accompagnement stratégique'] },
    immobilier: { name: 'Immobilier direct', type: 'Investissement', icon: Building2, status: 'Actif', statusColor: 'text-emerald-500/70', description: 'Investissement immobilier en nom propre, hors structures SCI.', details: ['Résidentiel', 'Locatif', 'Plus-value à terme'] }
  };
  
  const item = data[id];
  if (loading || !user) return null;
  if (!item) { navigate('/capital'); return null; }
  const Icon = item.icon;

  return (
    <div className="min-h-screen bg-black">
      <GrainOverlay /><Vignette /><AuthHeader />
      <main className="relative z-10 max-w-4xl mx-auto px-8 py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="space-y-12">
          <button onClick={() => navigate('/capital')} className="inline-flex items-center gap-2 text-zinc-500 text-sm hover:text-white transition-colors duration-300"><ArrowLeft className="w-4 h-4" />Archeon Capital</button>
          <div className="space-y-4">
            <div className="flex items-center gap-4"><Icon className="w-10 h-10 text-amber-400" /><span className={`text-xs tracking-[0.15em] uppercase ${item.statusColor}`}>{item.status}</span></div>
            <h1 className="text-4xl md:text-5xl font-bold text-white">{item.name}</h1>
            <p className="text-zinc-500 text-lg">{item.type}</p>
          </div>
          <p className="text-zinc-400 leading-relaxed text-lg border-l-2 border-zinc-800 pl-6">{item.description}</p>
          <div className="space-y-6">
            <h2 className="text-zinc-600 text-xs tracking-[0.2em] uppercase">Détails</h2>
            <ul className="space-y-3">{item.details.map((d, i) => <li key={i} className="flex items-center gap-3 text-zinc-400"><span className="w-1.5 h-1.5 bg-amber-500/50 rounded-full"></span>{d}</li>)}</ul>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

// Admin Page (superadmin only)
const AdminPage = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [users, setUsers] = useState([]);
  const [newUsername, setNewUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [validityDays, setValidityDays] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
      } else {
        console.error('Failed to fetch users');
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (user && user.role === 'superadmin') {
      fetchUsers();
    }
  }, [user]);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setIsCreating(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/admin/users`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          username: newUsername,
          password: newPassword,
          validity_days: validityDays ? parseInt(validityDays) : null
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Erreur lors de la création");
      }

      setSuccess("Utilisateur créé avec succès");
      setNewUsername("");
      setNewPassword("");
      setValidityDays("");
      fetchUsers();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`Supprimer l'utilisateur "${username}" ?`)) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/admin/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Erreur lors de la suppression");
      }

      setError("");
      fetchUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) return null;
  
  if (!user || user.role !== 'superadmin') {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <GrainOverlay /><Vignette />
        <div className="text-center">
          <p className="text-zinc-500 mb-4">Accès non autorisé</p>
          <button onClick={() => navigate('/dashboard')} className="text-zinc-400 hover:text-white transition-colors">Retour au dashboard</button>
        </div>
      </div>
    );
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatExpiry = (expiresAt) => {
    if (!expiresAt) return 'Illimité';
    const date = new Date(expiresAt);
    const now = new Date();
    if (date < now) return 'Expiré';
    return formatDate(expiresAt);
  };

  return (
    <div className="min-h-screen bg-black">
      <GrainOverlay /><Vignette />
      <AuthHeader />
      <main className="relative z-10 max-w-4xl mx-auto px-8 py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="space-y-12">
          
          {/* Header */}
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-zinc-600" />
              <h1 className="text-3xl font-bold text-white">Console Admin</h1>
            </div>
            <p className="text-zinc-600 text-sm">Gestion des utilisateurs</p>
          </div>

          {/* Create User Form */}
          <div className="border border-zinc-800 p-6 space-y-6">
            <h2 className="text-zinc-500 text-xs tracking-[0.15em] uppercase">Créer un utilisateur</h2>
            
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <label className="block text-zinc-600 text-xs tracking-[0.1em] uppercase">Identifiant</label>
                  <input
                    type="text"
                    value={newUsername}
                    onChange={(e) => setNewUsername(e.target.value)}
                    required
                    className="w-full bg-transparent border border-zinc-800 text-white px-4 py-2.5 text-sm focus:outline-none focus:border-zinc-600 transition-colors"
                    placeholder="nom.utilisateur"
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-zinc-600 text-xs tracking-[0.1em] uppercase">Mot de passe</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    className="w-full bg-transparent border border-zinc-800 text-white px-4 py-2.5 text-sm focus:outline-none focus:border-zinc-600 transition-colors"
                    placeholder="••••••••"
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-zinc-600 text-xs tracking-[0.1em] uppercase">Validité (jours)</label>
                  <input
                    type="number"
                    value={validityDays}
                    onChange={(e) => setValidityDays(e.target.value)}
                    min="1"
                    className="w-full bg-transparent border border-zinc-800 text-white px-4 py-2.5 text-sm focus:outline-none focus:border-zinc-600 transition-colors"
                    placeholder="Vide = illimité"
                  />
                </div>
              </div>

              {error && <p className="text-red-500 text-sm">{error}</p>}
              {success && <p className="text-emerald-500 text-sm">{success}</p>}

              <button
                type="submit"
                disabled={isCreating}
                className="inline-flex items-center gap-2 px-4 py-2.5 border border-zinc-700 text-white text-sm hover:bg-zinc-900 transition-colors disabled:opacity-50"
              >
                <Plus className="w-4 h-4" />
                {isCreating ? "Création..." : "Créer"}
              </button>
            </form>
          </div>

          {/* Users List */}
          <div className="space-y-6">
            <h2 className="text-zinc-500 text-xs tracking-[0.15em] uppercase">Utilisateurs ({users.length})</h2>
            
            <div className="border border-zinc-800 divide-y divide-zinc-800">
              {users.map((u) => (
                <div key={u.id} className="p-4 flex items-center justify-between hover:bg-zinc-950 transition-colors">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <p className="text-white font-medium">{u.username}</p>
                      {u.role === 'superadmin' && (
                        <span className="text-xs px-2 py-0.5 bg-amber-500/20 text-amber-500 tracking-wider uppercase">Admin</span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 mt-1">
                      <span className="text-zinc-600 text-xs">Créé: {formatDate(u.created_at)}</span>
                      <span className={`text-xs ${u.expires_at && new Date(u.expires_at) < new Date() ? 'text-red-500' : 'text-zinc-600'}`}>
                        Expire: {formatExpiry(u.expires_at)}
                      </span>
                    </div>
                  </div>
                  {u.role !== 'superadmin' && (
                    <button
                      onClick={() => handleDeleteUser(u.id, u.username)}
                      className="p-2 text-zinc-600 hover:text-red-500 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

        </motion.div>
      </main>
    </div>
  );
};

// App
function App() {
  return (
    <div className="App bg-black min-h-screen" data-testid="app-container">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/connexion" element={<LoginPage />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/education" element={<EducationPage />} />
          <Route path="/education/:id" element={<EducationVerticalPage />} />
          <Route path="/education/:id/:projectId" element={<EducationProjectPage />} />
          <Route path="/technologies" element={<TechnologiesPage />} />
          <Route path="/technologies/apps" element={<TechAppsPage />} />
          <Route path="/technologies/apps/:id" element={<TechAppDetailPage />} />
          <Route path="/technologies/web" element={<TechWebPage />} />
          <Route path="/technologies/systems" element={<TechSystemsPage />} />
          <Route path="/technologies/privacy" element={<TechPrivacyPage />} />
          <Route path="/technologies/gaming" element={<TechGamingPage />} />
          <Route path="/technologies/gaming/:id" element={<TechGamingDetailPage />} />
          <Route path="/technologies/gaming/:id/:serverId" element={<TechGamingServerPage />} />
          <Route path="/technologies/community" element={<TechCommunityPage />} />
          <Route path="/capital" element={<CapitalPage />} />
          <Route path="/capital/:id" element={<CapitalDetailPage />} />
          <Route path="/admin" element={<AdminPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
